
import type { ApiError, ConflictApiError, RelatedTags } from '@empathyco/playboard-core-domain'
import type { SetupContext } from 'vue'
import type { RegularFilter, RegularFilterSelectedValues } from '../../models'
import type { ResolveConflictOption } from '../../models/components/resolve-conflict-option'
import type { DomainServiceProvider } from '../../providers/interfaces'
import type { RelatedTagsDomainService } from '../../services/interfaces'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import ConflictErrorModalWithOptions from '../../components/crud/conflict-error-modal/conflict-error-modal-with-options.component.vue'
import { UiAlert } from '../../components/keen-ui'
import RelatedTagsConfig from '../../components/related-tags/related-tags-config.component.vue'
import { EpbTextbox } from '../../components/shared/epb-textbox'
import ToolingFormLayout from '../../components/tooling/tooling-form-layout.component.vue'
import {
  useApiError,
  useCurationToolCriteria,
  useToolingForm,
  useUpdateItemWithContext,
} from '../../composables'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { FeatureIdEnum } from '../../enums'
import { ROUTER_CONSTS } from '../../router/router.consts'

export default defineComponent({
  name: 'RelatedTagsForm',
  components: {
    EpbTextbox,
    ToolingFormLayout,
    UiAlert,
    RelatedTagsConfig,
    ConflictErrorModalWithOptions,
  },
  emits: ['resetScroll', 'updateLoading', 'showError'],
  setup(props, ctx: SetupContext) {
    const { $epb, $route } = getCurrentInstance().proxy

    const messages = $epb.messages.tools.relatedTags

    const updateMode = $route.name === ROUTER_CONSTS.routes[FeatureIdEnum.RELATED_TAGS].update
    const { updateItemWithContext } = useUpdateItemWithContext<RelatedTags>()
    const toolingForm = useToolingForm<RelatedTags>(props, ctx, {
      messages,
      updateMode,
      pivotsExtraConfig: {
        default: {
          mandatory: false,
          multiselect: true,
        },
        lang: {
          mandatory: true,
          multiselect: false,
        },
      },
    })

    const {
      conflictApiError,
      updatePivots,
      setCriteria,
      itemQuery,
      normalizedQuery,
      ...curationToolCriteria
    } = useCurationToolCriteria({
      featureId: toolingForm.featureId,
      item: toolingForm.item,
      pivotsConfig: toolingForm.pivotsConfig,
    })

    const { getErrorMessage, isConflictError } = useApiError()

    const domainService = CONTAINER.get<DomainServiceProvider>(TYPES.DomainServiceProvider)(
      toolingForm.featureId.value,
    ) as RelatedTagsDomainService

    const baseClass = 'epb-related-tags-form'

    const resolveConflictModal = ref()

    const contextChangeType = ref<'query' | 'pivot' | ''>('')

    const areRelatedTagsLoading = ref(false)

    const query = ref<typeof itemQuery.value>(undefined)

    const changedCriteria = ref<RelatedTags['criteria'] | undefined>(undefined)

    const isValid = computed<boolean>(() =>
      domainService.isValid(toolingForm.item.value, {
        pivotsConfig: toolingForm.pivotsConfig.value,
      }),
    )

    async function confirmAccept(updatePrevious?: RelatedTags): Promise<void> {
      const queryToSave = itemQuery.value
      toolingForm.item.value.criteria.queries = [queryToSave]

      await toolingForm.onAccept({ errorHandler }, updatePrevious)
    }

    function errorHandler(error: ApiError): void {
      if (isConflictError(error)) {
        ;(error as ConflictApiError).conflictingElements.map(conflictingElement => {
          conflictingElement.queries = [normalizedQuery.value || itemQuery.value]
        })
        conflictApiError.value = error as ConflictApiError
        resolveConflictModal.value.open()
      } else {
        ctx.emit('showError', getErrorMessage(error))
      }
    }

    async function accept(): Promise<void> {
      await confirmAccept()
    }

    function cancel(): void {
      toolingForm.onCancel()
    }

    async function confirmCriteriaChange(preserveRules = false): Promise<void> {
      try {
        areRelatedTagsLoading.value = true
        //This needs to happen after the rules check inside changeCriteria or it won't display confirm dialog
        if (contextChangeType.value === 'query') {
          toolingForm.item.value.data.rules = []
          toolingForm.item.value.data.indexed = []
        }
        await setCriteria(changedCriteria.value, preserveRules)
      } catch (error) {
        ctx.emit('showError', getErrorMessage(error as ApiError))
      } finally {
        areRelatedTagsLoading.value = false
      }
    }

    async function changeCriteria(
      criteria: RelatedTags['criteria'],
      preserveRules = false,
    ): Promise<void> {
      changedCriteria.value = criteria
      if (!toolingForm.item.value.data.rules.length || preserveRules) {
        await confirmCriteriaChange(preserveRules)
      } else {
        await confirmCriteriaChange(contextChangeType.value === 'pivot')
      }
    }

    async function onEnabledChange(value: boolean): Promise<void> {
      const criteria = {
        ...toolingForm.item.value.criteria,
        enabled: value,
      }

      changedCriteria.value = criteria

      await setCriteria(changedCriteria.value, true)
    }

    function onQueryChange(value: string): Promise<void> {
      contextChangeType.value = 'query'
      return changeCriteria({
        ...toolingForm.item.value.criteria,
        queries: value ? [value] : [],
      })
    }

    function onPivotChange(
      filter: RegularFilter,
      selectedValues: RegularFilterSelectedValues,
    ): Promise<void> {
      contextChangeType.value = 'pivot'
      const pivots = updatePivots(toolingForm.item.value.criteria.pivots, filter, selectedValues)
      return changeCriteria({
        ...toolingForm.item.value.criteria,
        pivots,
      })
    }

    async function resolveConflict(resolveConflictOption: ResolveConflictOption): Promise<void> {
      await toolingForm.resolveConflict({
        resolveConflictOption,
        errorHandler,
      })
    }

    toolingForm
      .initItem({ forceFetch: true })
      .then(async () => {
        if (!updateMode) {
          await changeCriteria(toolingForm.item.value.criteria, true)

          toolingForm.item.value.data.rules.forEach(rule => {
            rule.id = undefined
          })
        }
      })
      .finally(async () => {
        const isCreationMode = !updateMode
        if (isCreationMode && !toolingForm.isCloneMode()) {
          await updateItemWithContext(toolingForm.item)
          query.value = toolingForm.item.value.criteria.queries.at(0)

          if (query.value) {
            await onQueryChange(query.value)
          }
        }
      })

    watch(
      itemQuery,
      value => {
        query.value = value
      },
      { immediate: true },
    )

    return {
      ...toolingForm,
      ...curationToolCriteria,
      accept,
      baseClass,
      cancel,
      confirmCriteriaChange,
      query,
      conflictApiError,
      isValid,
      itemQuery,
      messages,
      normalizedQuery,
      onEnabledChange,
      onPivotChange,
      onQueryChange,
      relatedTagsUpdateRoute: ROUTER_CONSTS.routes[FeatureIdEnum.RELATED_TAGS].update,
      resolveConflict,
      resolveConflictModal,
      updateMode,
      areRelatedTagsLoading,
      contextChangeType,
    }
  },
})
